import { AudioTone } from "../types";

let activeContext: AudioContext | null = null;
let activeAudio: HTMLAudioElement | null = null;

export const stopAudioTone = (): void => {
  if (activeAudio) {
    activeAudio.pause();
    activeAudio.currentTime = 0;
    activeAudio = null;
  }
};

const normalizeVolume = (volume = 1): number => Math.min(Math.max(volume, 0), 1);

export const setActiveAudioToneVolume = (volume = 1): void => {
  if (!activeAudio) return;

  activeAudio.volume = normalizeVolume(volume);
};

export const playAudioTone = async (tone: AudioTone, volume = 1): Promise<void> => {
  if (typeof window === "undefined") return;

  stopAudioTone();

  if (tone.src) {
    activeAudio = new Audio(tone.src);
    activeAudio.volume = normalizeVolume(volume);
    activeAudio.currentTime = 0;
    activeAudio.addEventListener("ended", () => {
      activeAudio = null;
    });
    await activeAudio.play();
    return;
  }

  if (!tone.frequencies?.length) return;

  const AudioContextConstructor = window.AudioContext || (window as any).webkitAudioContext;
  if (!AudioContextConstructor) return;

  if (!activeContext) {
    activeContext = new AudioContextConstructor();
  }

  if (activeContext.state === "suspended") {
    await activeContext.resume();
  }

  const now = activeContext.currentTime;
  const durationMs = tone.durationMs ?? 260;
  const segmentDuration = Math.max(durationMs / tone.frequencies.length / 1000, 0.08);

  tone.frequencies.forEach((frequency, index) => {
    const oscillator = activeContext!.createOscillator();
    const gain = activeContext!.createGain();
    const start = now + index * segmentDuration;
    const end = start + segmentDuration * 0.88;

    oscillator.type = "sine";
    oscillator.frequency.setValueAtTime(frequency, start);
    gain.gain.setValueAtTime(0.0001, start);
    gain.gain.exponentialRampToValueAtTime(0.12 * normalizeVolume(volume), start + 0.02);
    gain.gain.exponentialRampToValueAtTime(0.0001, end);

    oscillator.connect(gain);
    gain.connect(activeContext!.destination);
    oscillator.start(start);
    oscillator.stop(end + 0.02);
  });
};
